// Excel export of a project's recipient list. The rows match the "Recipient
// management" table on the project details page: deleted recipients are left out
// and the list is sorted by e-mail domain.

import type { Cell, SheetData } from "write-excel-file/browser";
import { IRecipientsManagement } from "../models/IRecipientsManagement";
import { compareByEmailDomain, getEmailDomain } from "./recipientDomains";

// Widths are in characters.
const columns = [
  { header: "Domain", width: 24 },
  { header: "E-mail", width: 36 },
  { header: "First name", width: 18 },
  { header: "Last name", width: 18 },
  { header: "Company", width: 28 },
  { header: "Invitation sent", width: 16 },
  { header: "Terms accepted", width: 16 },
  { header: "Acceptance date", width: 18 },
];

const yesNo = (value: boolean): string => (value ? "Yes" : "No");

// Excel dates carry no time zone and the library converts a Date as UTC, so the
// local time is moved into UTC to make Excel show the time the table shows.
export const toExcelDate = (value?: Date | string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (isNaN(date.getTime())) return undefined;
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );
};

// Text values are written as text cells, so a name such as "=HYPERLINK(...)"
// entered by an external recipient is never evaluated as a formula.
export const getRecipientExportData = (
  recipients?: IRecipientsManagement[],
): SheetData => {
  const header = columns.map(
    (column): Cell => ({ value: column.header, fontWeight: "bold" }),
  );
  const rows = (recipients || [])
    .filter((recipient) => recipient.isDeleted != true)
    .sort(compareByEmailDomain)
    .map((recipient): Cell[] => {
      const acceptanceDate = toExcelDate(recipient.acceptanceDate);
      return [
        getEmailDomain(recipient.email),
        recipient.email,
        recipient.name || null,
        recipient.lastName || null,
        recipient.companyName || null,
        yesNo(recipient.sentTermsCondition),
        yesNo(recipient.termsConditionAccepted || !!acceptanceDate),
        acceptanceDate
          ? { value: acceptanceDate, type: Date, format: "dd.mm.yyyy hh:mm" }
          : null,
      ];
    });
  return [header, ...rows];
};

export const getRecipientExportFileName = (
  projectName?: string,
  date: Date = new Date(),
): string => {
  // Replace the characters Windows does not allow in file names.
  const name =
    Array.from(projectName || "")
      .map((character) =>
        character < " " || '\\/:*?"<>|'.includes(character) ? " " : character,
      )
      .join("")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 100) || "Project";
  const day =
    date.getFullYear() +
    "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + date.getDate()).slice(-2);
  return `${name} - Recipients - ${day}.xlsx`;
};

export const exportRecipientsToExcel = async (
  projectName?: string,
  recipients?: IRecipientsManagement[],
): Promise<void> => {
  // Loaded on demand, so the library is only downloaded when someone exports.
  const { default: writeExcelFile } = await import("write-excel-file/browser");
  await writeExcelFile(getRecipientExportData(recipients), {
    sheet: "Recipients",
    columns: columns.map(({ width }) => ({ width })),
    stickyRowsCount: 1,
  }).toFile(getRecipientExportFileName(projectName));
};
